import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';
import { EntityPickerModalComponent } from 'src/app/components/entity-picker-modal/entity-picker-modal.component';

type Mod = 'csoport' | 'osztaly';

/** Egy tábla forrása: osztály, csoport, vagy a besorolatlanok „gyűjtője”. */
interface Egyseg {
  tipus: 'osztaly' | 'csoport' | 'nincs-osztaly' | 'nincs-csoport';
  id: string;
  nev: string;
}

interface Sor {
  oktazon: string;
  nev: string;
  osztaly: string | null;
  kezdete: string | null;
  vege: string | null;
  csoport_db: number;
  jelolt?: boolean;
}

interface Tabla {
  egyseg: Egyseg | null;
  sorok: Sor[];
  kereses: string;
  betolt: boolean;
}

const NINCS_OSZTALY: Egyseg = { tipus: 'nincs-osztaly', id: 'nincs-osztaly', nev: 'Osztályba nem sorolt tanulók' };
const NINCS_CSOPORT: Egyseg = { tipus: 'nincs-csoport', id: 'nincs-csoport', nev: 'Egyetlen csoportban sem lévő tanulók' };

function maiNap(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 *  BESOROLÁS KÉT TÁBLÁVAL – a KRÉTA „Osztályba sorolás” / „Csoportba sorolás” mintájára.
 *
 *  Balra a FORRÁS (egy osztály, egy csoport, vagy a besorolatlan tanulók), jobbra a CÉL
 *  a mostani tagjaival. A kijelölt tanulók egy gombnyomással mennek át – dátummal:
 *
 *    →  besorolás (a forrás osztály/besorolatlan) vagy átsorolás (a forrás csoport/osztály:
 *       a régi tagság a dátum előtti nappal zárul, az új a dátumtól kezdődik);
 *    ←  vissza a forrásba (ha az csoport/osztály), különben kisorolás a célból.
 *
 *  A meglévő két besorolás-képernyő és az egyenkénti Átsorolás változatlanul megmarad; ez a
 *  képernyő ugyanazokat a szabályokat hívja (tanuloi-csoport.js `/athelyezes`,
 *  atsorolas.js `/osztalyba`).
 */
@Component({
  selector: 'app-admin-besorolas-ket-tabla',
  templateUrl: './besorolas-ket-tabla.component.html',
  styleUrls: ['./besorolas-ket-tabla.component.scss'],
})
export class AdminBesorolasKetTablaComponent implements OnInit {
  intezmeny = '';
  munkatanev: number | null = null;
  mod: Mod = 'csoport';
  csoportJog = false;
  osztalyJog = false;
  jogBetoltve = false;

  osztalyok: any[] = [];
  csoportok: any[] = [];

  bal: Tabla = { egyseg: null, sorok: [], kereses: '', betolt: false };
  jobb: Tabla = { egyseg: null, sorok: [], kereses: '', betolt: false };

  /** A belépés / átsorolás / kilépés napja – alapból ma, a tanéven belül. */
  datum = maiNap();
  /** Csoport → csoport: a forráscsoportban is maradjon (csak hozzáadás). */
  forrasbanMarad = false;
  /** Osztályváltásnál a régi osztályhoz tartozó csoportok is mozduljanak. */
  csoportokkal = true;
  fut = false;

  constructor(private http: HttpClient, private data: DataService, private modalCtrl: ModalController) {}

  async ngOnInit() {
    const si = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || si?.id || '').trim().replace(/^\/+|\/+$/g, '');
    this.munkatanev = await this.data.getMunkatanev();
    try {
      const jog = await this.data.hozzaferesek();
      const sajat = jog.szabad('/admin/besorolas-ket-tabla');
      this.csoportJog = sajat || jog.szabad('/admin/csoport-besorolas');
      this.osztalyJog = sajat || jog.szabad('/admin/osztaly-besorolas');
    } catch (_) {
      this.csoportJog = this.osztalyJog = false;
    }
    this.jogBetoltve = true;
    if (!this.csoportJog && this.osztalyJog) this.mod = 'osztaly';
    this.datum = this.datumTanevbe(maiNap());
    await this.torzsBetoltes();
    this.modValtas();
  }

  get intezmenyPath(): string { return encodeURI(this.intezmeny); }

  private get evParam(): string { return this.munkatanev != null ? `?ev=${this.munkatanev}` : ''; }

  /** A tanév határai a dátummezőhöz (a kiszolgáló a pontos tanévkezdetet is nézi). */
  get tanevHatar(): { min: string; max: string } | null {
    const ev = Number(this.munkatanev) || 0;
    return ev ? { min: `${ev}-09-01`, max: `${ev + 1}-08-31` } : null;
  }

  private datumTanevbe(d: string): string {
    const h = this.tanevHatar;
    if (!h) return d;
    return d < h.min ? h.min : (d > h.max ? h.max : d);
  }

  async torzsBetoltes() {
    if (!this.intezmeny) return;
    try {
      const [osztalyok, csoportok]: any[] = await Promise.all([
        firstValueFrom(this.http.get<any[]>(`/api/tanuloi-osztaly/osztalyok/${this.intezmenyPath}${this.evParam}`)),
        firstValueFrom(this.http.get<any[]>(`/api/tanuloi-csoport/csoportok/${this.intezmenyPath}${this.evParam}`)),
      ]);
      this.osztalyok = (Array.isArray(osztalyok) ? osztalyok : [])
        .sort((a: any, b: any) => (Number(a.evfolyam) - Number(b.evfolyam))
          || String(a.osztaly).localeCompare(String(b.osztaly), 'hu'));
      this.csoportok = (Array.isArray(csoportok) ? csoportok : [])
        .sort((a: any, b: any) => String(a.name).localeCompare(String(b.name), 'hu'));
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni az osztályokat és a csoportokat.', 'error');
    }
  }

  modValtas() {
    this.bal = { egyseg: this.mod === 'csoport' ? NINCS_CSOPORT : NINCS_OSZTALY, sorok: [], kereses: '', betolt: false };
    this.jobb = { egyseg: null, sorok: [], kereses: '', betolt: false };
    this.forrasbanMarad = false;
    this.tablaBetoltes(this.bal);
  }

  // ── Választás ─────────────────────────────────────────────────────────────

  private osztalyEgyseg(o: any): Egyseg { return { tipus: 'osztaly', id: String(o.id), nev: String(o.osztaly) }; }
  private csoportEgyseg(c: any): Egyseg { return { tipus: 'csoport', id: String(c.id), nev: String(c.name) }; }

  /** Bal oldal: csoport módban osztály, csoport vagy „egyik csoportban sem”; osztály módban osztály vagy besorolatlan. */
  async forrasValasztas() {
    const elemek: { e: Egyseg; sub?: string }[] = [];
    if (this.mod === 'csoport') {
      elemek.push({ e: NINCS_CSOPORT, sub: 'besoroláshoz' });
      for (const o of this.osztalyok) elemek.push({ e: this.osztalyEgyseg(o), sub: `osztály · ${o.tanulo_db || 0} tanuló` });
      for (const c of this.csoportok) elemek.push({ e: this.csoportEgyseg(c), sub: `csoport · ${c.tanulo_db || 0} tanuló` });
    } else {
      elemek.push({ e: NINCS_OSZTALY, sub: 'besoroláshoz' });
      for (const o of this.osztalyok) elemek.push({ e: this.osztalyEgyseg(o), sub: `osztály · ${o.tanulo_db || 0} tanuló` });
    }
    const v = await this.valaszt('Forrás (bal oldal)', elemek, this.bal.egyseg);
    if (!v) return;
    this.bal = { egyseg: v, sorok: [], kereses: '', betolt: false };
    if (v.tipus !== 'csoport') this.forrasbanMarad = false;
    await this.tablaBetoltes(this.bal);
  }

  /** Jobb oldal: a cél csoport, illetve osztály. */
  async celValasztas() {
    const elemek = this.mod === 'csoport'
      ? this.csoportok.map((c) => ({ e: this.csoportEgyseg(c), sub: `${c.tanulo_db || 0} tanuló` }))
      : this.osztalyok.map((o) => ({ e: this.osztalyEgyseg(o), sub: `${o.tanulo_db || 0} tanuló` }));
    const v = await this.valaszt(this.mod === 'csoport' ? 'Célcsoport' : 'Célosztály', elemek, this.jobb.egyseg);
    if (!v) return;
    this.jobb = { egyseg: v, sorok: [], kereses: '', betolt: false };
    await this.tablaBetoltes(this.jobb);
  }

  private async valaszt(cim: string, elemek: { e: Egyseg; sub?: string }[], jelenlegi: Egyseg | null): Promise<Egyseg | null> {
    const kulcs = (e: Egyseg) => `${e.tipus}:${e.id}`;
    const modal = await this.modalCtrl.create({
      component: EntityPickerModalComponent,
      componentProps: {
        title: cim,
        searchPlaceholder: 'Név…',
        multiple: false,
        selectedIds: jelenlegi ? [kulcs(jelenlegi)] : [],
        items: elemek.map((x) => ({ id: kulcs(x.e), label: x.e.nev, sublabel: x.sub })),
      },
    });
    await modal.present();
    const { data, role } = await modal.onWillDismiss();
    if (role !== 'confirm' || !data?.ids?.length) return null;
    return elemek.find((x) => kulcs(x.e) === data.ids[0])?.e || null;
  }

  // ── Táblák ────────────────────────────────────────────────────────────────

  async tablaBetoltes(t: Tabla) {
    if (!t.egyseg || !this.intezmeny) return;
    t.betolt = true;
    try {
      const p = new URLSearchParams({ tipus: t.egyseg.tipus });
      if (t.egyseg.tipus === 'osztaly' || t.egyseg.tipus === 'csoport') p.set('id', t.egyseg.id);
      if (this.munkatanev != null) p.set('ev', String(this.munkatanev));
      const r: any = await firstValueFrom(this.http.get(`/api/atsorolas/lista/${this.intezmenyPath}?${p.toString()}`));
      t.sorok = (Array.isArray(r?.tanulok) ? r.tanulok : []).map((s: Sor) => ({ ...s, jelolt: false }));
    } catch (e: any) {
      t.sorok = [];
      this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni a tanulókat.', 'error');
    } finally {
      t.betolt = false;
    }
  }

  private normal(v: any): string {
    return String(v ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  }

  szurt(t: Tabla): Sor[] {
    const k = this.normal(t.kereses);
    if (!k) return t.sorok;
    return t.sorok.filter((s) => [s.nev, s.oktazon, s.osztaly].some((v) => this.normal(v).includes(k)));
  }

  /** A célban már bent lévő tanuló a bal oldalon nem jelölhető (nincs mit áttenni). */
  celbanVan(s: Sor): boolean {
    return !!this.jobb.egyseg && this.jobb.sorok.some((j) => j.oktazon === s.oktazon);
  }

  osszesJelolese(t: Tabla, ertek: boolean) {
    for (const s of this.szurt(t)) s.jelolt = ertek && !(t === this.bal && this.celbanVan(s));
  }

  sorKattintas(s: Sor, balOldal: boolean) {
    if (balOldal && this.celbanVan(s)) return;
    s.jelolt = !s.jelolt;
  }

  jeloltek(t: Tabla): Sor[] { return t.sorok.filter((s) => s.jelolt); }

  osszesJelolt(t: Tabla): boolean {
    const l = this.szurt(t).filter((s) => !(t === this.bal && this.celbanVan(s)));
    return l.length > 0 && l.every((s) => s.jelolt);
  }

  // ── Műveletek ─────────────────────────────────────────────────────────────

  get balraFelirat(): string {
    const b = this.bal.egyseg;
    const vissza = this.mod === 'csoport' ? b?.tipus === 'csoport' : b?.tipus === 'osztaly';
    return vissza ? 'Átsorolás vissza' : 'Kisorolás';
  }

  get jobbraFelirat(): string {
    const b = this.bal.egyseg;
    const at = this.mod === 'csoport' ? (b?.tipus === 'csoport' && !this.forrasbanMarad) : b?.tipus === 'osztaly';
    return at ? 'Átsorolás' : 'Besorolás';
  }

  private datumRendben(): boolean {
    const h = this.tanevHatar;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(this.datum || '')) {
      this.data.sendToast('Adj meg dátumot.', 'warning');
      return false;
    }
    if (h && (this.datum < h.min || this.datum > h.max)) {
      this.data.sendToast(`A dátum a tanéven belül legyen (${h.min} – ${h.max}).`, 'warning');
      return false;
    }
    return true;
  }

  /** Bal → jobb: besorolás vagy átsorolás a célba. */
  async jobbra() {
    const tanulok = this.jeloltek(this.bal).map((s) => s.oktazon);
    const cel = this.jobb.egyseg;
    if (!cel || !tanulok.length || !this.datumRendben()) return;
    const forras = this.bal.egyseg;
    if (this.mod === 'csoport') {
      const forrasCsoport = forras?.tipus === 'csoport' ? forras.id : '';
      const at = !!forrasCsoport && !this.forrasbanMarad;
      const szoveg = at
        ? `${tanulok.length} tanuló átsorolása: „${forras?.nev}” → „${cel.nev}”.\n\n`
          + `A(z) „${forras?.nev}” csoportban a tagság ${this.elozoNap(this.datum)}-ig tart, `
          + `a(z) „${cel.nev}” csoportban ${this.datum}-tól tag.`
        : `${tanulok.length} tanuló besorolása a(z) „${cel.nev}” csoportba ${this.datum}-tól.`;
      if (!confirm(`${szoveg}\n\nFolytatod?`)) return;
      await this.csoportMuvelet({ tanulok, forras: forrasCsoport, cel: cel.id, marad: this.forrasbanMarad });
    } else {
      const szoveg = `${tanulok.length} tanuló ${forras?.tipus === 'osztaly' ? 'átsorolása' : 'besorolása'} `
        + `a(z) ${cel.nev} osztályba ${this.datum}-tól. A régi osztálybesorolás ${this.elozoNap(this.datum)}-ig tart.`
        + (this.csoportokkal ? '\nA régi osztályhoz tartozó csoportok is lezárulnak (és ha van párjuk, oda kerülnek).' : '');
      if (!confirm(`${szoveg}\n\nFolytatod?`)) return;
      await this.osztalyMuvelet(tanulok, cel.id);
    }
  }

  /** Jobb → bal: vissza a forrás csoportba/osztályba, vagy kisorolás a célból. */
  async balra() {
    const tanulok = this.jeloltek(this.jobb).map((s) => s.oktazon);
    const cel = this.jobb.egyseg;
    if (!cel || !tanulok.length || !this.datumRendben()) return;
    const forras = this.bal.egyseg;
    if (this.mod === 'csoport') {
      const vissza = forras?.tipus === 'csoport' ? forras.id : '';
      const szoveg = vissza
        ? `${tanulok.length} tanuló átsorolása: „${cel.nev}” → „${forras?.nev}” (${this.datum}-tól).`
        : `${tanulok.length} tanuló kisorolása a(z) „${cel.nev}” csoportból: az utolsó nap ${this.elozoNap(this.datum)}.`;
      if (!confirm(`${szoveg}\n\nFolytatod?`)) return;
      await this.csoportMuvelet({ tanulok, forras: cel.id, cel: vissza, marad: false });
    } else {
      const vissza = forras?.tipus === 'osztaly' ? forras.id : '';
      const szoveg = (vissza
        ? `${tanulok.length} tanuló átsorolása a(z) ${forras?.nev} osztályba ${this.datum}-tól.`
        : `${tanulok.length} tanuló kisorolása a(z) ${cel.nev} osztályból: az utolsó nap ${this.elozoNap(this.datum)}. `
          + 'A tanuló az intézmény tanulója marad, osztály nélkül (a távozó tanulóhoz a Kiléptetés való).')
        + (this.csoportokkal ? '\nA régi osztályhoz tartozó csoportok is lezárulnak.' : '');
      if (!confirm(`${szoveg}\n\nFolytatod?`)) return;
      await this.osztalyMuvelet(tanulok, vissza);
    }
  }

  private elozoNap(d: string): string {
    const [y, m, n] = d.split('-').map(Number);
    const x = new Date(y, m - 1, n - 1);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  }

  private async csoportMuvelet(body: { tanulok: string[]; forras: string; cel: string; marad: boolean }) {
    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(`/api/tanuloi-csoport/athelyezes/${this.intezmenyPath}`,
        { ...body, datum: this.datum }));
      const reszek = [
        r?.felvett ? `${r.felvett} felvéve` : '',
        r?.kivett ? `${r.kivett} kivéve` : '',
        r?.mar_tag ? `${r.mar_tag} már tag volt` : '',
        r?.kreta_atvezetendo ? `${r.kreta_atvezetendo} KRÉTA-ban átvezetendő` : '',
      ].filter(Boolean);
      this.data.sendToast(`Kész${reszek.length ? ': ' + reszek.join(', ') : ''}.`);
      await this.frissites();
    } catch (e: any) {
      this.data.sendToast(typeof e?.error?.error === 'string' ? e.error.error : 'A művelet nem sikerült.', 'error');
    } finally {
      this.fut = false;
    }
  }

  private async osztalyMuvelet(tanulok: string[], cel: string) {
    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(`/api/atsorolas/osztalyba/${this.intezmenyPath}`,
        { tanulok, cel, datum: this.datum, csoportokkal: this.csoportokkal }));
      const hibak = (r?.tetelek || []).filter((t: any) => !t.ok);
      const reszek = [
        `${r?.sikeres || 0} tanuló kész`,
        r?.csoport_lezart ? `${r.csoport_lezart} csoporttagság lezárva` : '',
        r?.csoport_uj ? `${r.csoport_uj} új csoporttagság` : '',
      ].filter(Boolean);
      this.data.sendToast(reszek.join(', ') + '.', hibak.length ? 'warning' : undefined);
      if (hibak.length) {
        const nev = (o: string) => [...this.bal.sorok, ...this.jobb.sorok].find((s) => s.oktazon === o)?.nev || o;
        alert(`${hibak.length} tanulónál nem sikerült:\n\n`
          + hibak.map((h: any) => `${nev(h.oktazon)}: ${h.hiba}`).join('\n'));
      }
      await this.frissites();
    } catch (e: any) {
      this.data.sendToast(typeof e?.error?.error === 'string' ? e.error.error : 'A művelet nem sikerült.', 'error');
    } finally {
      this.fut = false;
    }
  }

  private async frissites() {
    await Promise.all([this.tablaBetoltes(this.bal), this.tablaBetoltes(this.jobb), this.torzsBetoltes()]);
  }
}
